import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Save, RefreshCw, Loader2, ExternalLink, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettingsPanel({ token }) {
  const { brochureUrl: cachedUrl, setLocal } = useSiteSettings();
  const [brochureUrl, setBrochureUrl] = useState(cachedUrl);
  const [original, setOriginal] = useState(cachedUrl);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/settings`, { headers });
      const url = data?.brochure_url || "";
      setBrochureUrl(url);
      setOriginal(url);
      setUpdatedAt(data?.updated_at || null);
      setLocal({ brochure_url: url, updated_at: data?.updated_at || null });
    } catch (e) {
      toast.error("Could not load settings");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const isUrlValid = (s) => {
    if (!s) return true; // empty is allowed (clears the brochure)
    try {
      const u = new URL(s);
      return u.protocol === "http:" || u.protocol === "https:";
    } catch (e) {
      return false;
    }
  };

  const dirty = brochureUrl !== original;
  const valid = isUrlValid(brochureUrl);

  const save = async () => {
    if (!valid) { toast.error("Please paste a valid http(s) URL or leave it blank."); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/settings`, { brochure_url: brochureUrl.trim() }, { headers });
      const url = data?.brochure_url || "";
      setOriginal(url);
      setBrochureUrl(url);
      setUpdatedAt(data?.updated_at || null);
      setLocal({ brochure_url: url, updated_at: data?.updated_at || null });
      toast.success("Brochure link updated. Every Download button across the site is now using it.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setBrochureUrl(original);

  return (
    <div data-testid="admin-settings-panel" className="space-y-6">
      <div className="lux-card p-7 md:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] tracking-[0.24em] uppercase text-[#8A9A7B] font-semibold">Global Setting</p>
            <h3 className="font-display text-2xl md:text-3xl mt-2 text-[#3B312D]">Brochure / Deck Download Link</h3>
            <p className="mt-2 text-[14px] text-[#3B312D]/75 max-w-2xl leading-relaxed">
              Paste the public URL of your latest brochure or deck PDF. Every &ldquo;Download Brochure&rdquo; button across the
              homepage, footer, packages, services, portfolio, and every other page will use this link automatically.
            </p>
          </div>
          <button onClick={load} disabled={loading} className="btn-ghost" data-testid="admin-settings-refresh">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {loading ? "Loading…" : "Refresh"}
          </button>
        </div>

        <div className="mt-6">
          <label className="block">
            <span className="block text-[11px] tracking-[0.22em] uppercase text-[#8A9A7B] font-semibold mb-1.5">Brochure URL</span>
            <div className="relative">
              <LinkIcon size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#3B312D]/40 pointer-events-none" />
              <input
                type="url"
                inputMode="url"
                placeholder="https://your-storage.com/petals-promises-deck.pdf"
                value={brochureUrl}
                onChange={(e) => setBrochureUrl(e.target.value)}
                className="lux-input !pl-11"
                data-testid="admin-settings-brochure-url"
              />
            </div>
          </label>
          {!valid && (
            <p className="mt-2 text-[12.5px] text-[#B65980]" data-testid="admin-settings-invalid-msg">
              That doesn&apos;t look like a valid http or https URL.
            </p>
          )}
          {valid && brochureUrl && (
            <p className="mt-3 text-[12.5px] text-[#3B312D]/70 inline-flex items-center gap-2">
              Preview:
              <a href={brochureUrl} target="_blank" rel="noreferrer noopener" className="text-[#B65980] hover:underline inline-flex items-center gap-1" data-testid="admin-settings-preview-link">
                {brochureUrl} <ExternalLink size={11} />
              </a>
            </p>
          )}
          {!brochureUrl && original && (
            <p className="mt-3 text-[12.5px] text-[#3B312D]/70">
              Saving an empty value will hide the Download buttons site-wide until you paste a new link.
            </p>
          )}
          {updatedAt && (
            <p className="mt-3 text-[11px] text-[#3B312D]/50">Last updated: {new Date(updatedAt).toLocaleString()}</p>
          )}
        </div>

        <div className="mt-7 flex gap-3 flex-wrap">
          <button onClick={save} disabled={!dirty || !valid || saving} className="btn-primary" data-testid="admin-settings-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Saving…" : "Save changes"}
          </button>
          {dirty && (
            <button onClick={reset} className="btn-ghost" data-testid="admin-settings-reset">
              Reset
            </button>
          )}
        </div>
      </div>

      <div className="text-[12.5px] text-[#3B312D]/70 leading-relaxed max-w-2xl">
        <p className="font-semibold text-[#3B312D] mb-2">Where this link appears:</p>
        <ul className="space-y-1 list-disc pl-5">
          <li>Footer &ldquo;Download Brochure&rdquo; button</li>
          <li>Brochure CTA band (Home, Services, Packages, Portfolio, About, Venues, Process, Checklist, Blogs)</li>
          <li>Any future Download Brochure / Download Deck / View Brochure buttons added later</li>
        </ul>
      </div>
    </div>
  );
}
